import { Brand } from "./Brand";
import { GS1ParseError, classKey, identityKey, parseScan, toAiDataStr, type GS1Elements } from "@trail/core";
import { useCallback, useEffect, useRef, useState } from "react";
import { CameraView } from "./CameraView.js";
import { PhotoTest } from "./PhotoTest.js";
import { getDiagKey, setDiagKey } from "./diag/client.js";
import { isGs1Symbology, type CodeState } from "./scan/camera.js";
import { feedback, unlockAudio } from "./scan/feedback.js";
import { attachWedge } from "./scan/wedge.js";

type Result =
  | { ok: true; input: string; elements: GS1Elements; ms: number; merged?: boolean }
  | { ok: "plain"; input: string; symbology: string; text: string }
  | { ok: false; input: string; error: string; markup?: string | undefined };

/** The scan test page used in the field phase to validate real labels on real devices (T1).
 *  Reached from Settings; the task client lives in App.tsx. */
export function ScanTest({ onClose }: { onClose: () => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [results, setResults] = useState<Result[]>([]);
  const [scheme] = useState<"gtin+lot" | "gtin+lot+serial" | "gtin+serial" | "sscc">("gtin+lot");
  const [camera, setCamera] = useState(false);
  const [photoTest, setPhotoTest] = useState(() => new URLSearchParams(window.location.search).has("photo"));

  const handleScan = async (data: string): Promise<CodeState> => {
    const t0 = performance.now();
    // A code with a non-GS1 symbology identifier (plain Code 128, Code 39, plain QR) is
    // not an error: it is a plain identifier — e.g. a supplier's carton number — that the
    // system will use for lookup (spec §2.4). Only data that claims to be GS1 can be wrong.
    const aim = /^\]\w\w/.exec(data)?.[0];
    if (aim && !isGs1Symbology(data)) {
      feedback("attention");
      const result: Result = { ok: "plain", input: data, symbology: aim, text: data.slice(3) };
      setResults((r) => [result, ...r].slice(0, 20));
      return "plain";
    }
    try {
      const elements = await parseScan(data);
      feedback("accepted");
      const result: Result = { ok: true, input: data, elements, ms: Math.round(performance.now() - t0) };
      setResults((r) => [result, ...r].slice(0, 20));
      return "accepted";
    } catch (err) {
      feedback("rejected");
      const e = err as GS1ParseError;
      const result: Result = { ok: false, input: data, error: e.message, markup: e.markup };
      setResults((r) => [result, ...r].slice(0, 20));
      return "rejected";
    }
  };

  // Scan assembly (spec §3.3): a label may split its data over several codes. Codes
  // parsed within a short window whose AIs do not conflict are merged into one result.
  const recentRef = useRef<Array<{ at: number; elements: GS1Elements }>>([]);
  const ASSEMBLY_WINDOW_MS = 2500;

  const tryAssemble = async (elements: GS1Elements): Promise<GS1Elements | null> => {
    const now = performance.now();
    recentRef.current = recentRef.current.filter((r) => now - r.at < ASSEMBLY_WINDOW_MS);
    const complement = recentRef.current.find((r) => {
      const a = r.elements.ai;
      const b = elements.ai;
      const overlap = Object.keys(a).filter((k) => k in b);
      if (overlap.some((k) => a[k] !== b[k])) return false; // conflicting data: another label
      const adds = Object.keys(b).some((k) => !(k in a)) && Object.keys(a).some((k) => !(k in b));
      return adds; // each side contributes something the other lacks
    });
    recentRef.current.push({ at: now, elements });
    if (!complement) return null;
    const merged = { ...complement.elements.ai, ...elements.ai };
    try {
      return await parseScan(toAiDataStr(merged)); // typed fields from the union
    } catch {
      return null;
    }
  };


  // Partial-code policy (spec §3.3): "assemble-required" — a code without the GTIN the
  // identity scheme needs is held until a complementary code of the same label completes it.
  const partialRef = useRef<Array<{ data: string; elements: GS1Elements; at: number }>>([]);

  const onCameraCode = useCallback(async (data: string, _format: string, mark: (d: string, s: CodeState) => void): Promise<CodeState> => {
    const aim = /^\]\w\w/.exec(data)?.[0];
    if (aim && !isGs1Symbology(data)) return handleScan(data);
    try {
      const elements = await parseScan(data);
      const complete = identityKey("gtin+lot", elements) !== null || elements.sscc !== null;
      const merged = await tryAssemble(elements);
      if (merged) {
        // This code completed an earlier partial one (or vice versa): both are accepted.
        const now = performance.now();
        for (const p of partialRef.current.filter((p) => now - p.at < ASSEMBLY_WINDOW_MS)) mark(p.data, "accepted");
        partialRef.current = [];
        feedback("accepted");
        const result: Result = { ok: true, input: data, elements: merged, ms: 0, merged: true };
        setResults((r) => [result, ...r].slice(0, 20));
        return "accepted";
      }
      if (!complete) {
        // Hold: waiting for the GTIN code on the same label.
        partialRef.current.push({ data, elements, at: performance.now() });
        feedback("attention");
        return "partial";
      }
      feedback("accepted");
      const result: Result = { ok: true, input: data, elements, ms: 0 };
      setResults((r) => [result, ...r].slice(0, 20));
      return "accepted";
    } catch (err) {
      feedback("rejected");
      const e = err as GS1ParseError;
      const result: Result = { ok: false, input: data, error: e.message, markup: e.markup };
      setResults((r) => [result, ...r].slice(0, 20));
      return "rejected";
    }
  }, []);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    return attachWedge(el, { onScan: (d) => void handleScan(d) });
  }, []);

  const latest = results[0];

  if (camera) {
    return <CameraView onCode={onCameraCode} onClose={() => setCamera(false)} diagnostics />;
  }
  if (photoTest) {
    return <PhotoTest onClose={() => setPhotoTest(false)} />;
  }
  return (
    <main className="legacy" onClick={() => { unlockAudio(); inputRef.current?.focus(); }}>
      <Brand title="scan test" />
      <p className="muted build-stamp">Build {__BUILD_VERSION__} · {new Date(__BUILD_TIME__).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" })}</p>
      <div className="panel">
        <div className="row">
          <button type="button" onClick={(e) => { e.stopPropagation(); unlockAudio(); setCamera(true); }}>Open camera</button>
          <button type="button" className="secondary" onClick={(e) => { e.stopPropagation(); setPhotoTest(true); }}>Test on a photo</button>
          <button type="button" className="secondary" onClick={(e) => { e.stopPropagation(); onClose(); }}>Back</button>
        </div>
      </div>
      <details className="panel" onClick={(e) => e.stopPropagation()}>
        <summary className="muted">Diagnostics (for the shadow phase)</summary>
        <p className="muted">Enter the diagnostics key once; the camera view then shows the task list and a Capture button that uploads frames and timings.</p>
        <input className="diag-key" defaultValue={getDiagKey()} placeholder="Diagnostics key" onChange={(e) => setDiagKey(e.target.value)} autoComplete="off" autoCapitalize="off" />
      </details>
      <div className="panel">
        <input ref={inputRef} className="scanbox" inputMode="text" autoComplete="off" autoCorrect="off" autoCapitalize="characters" spellCheck={false} placeholder="Scan, or type (01)…(10)… and press Enter" aria-label="Scan input" />
        <p className="muted">
          Bluetooth or wedge scanners work as they are. To type a label by hand, enter the text printed under its barcode with the
          parentheses, e.g. <code>(01)05412340661837(15)280104(10)K49O8610(30)5040</code> — no separator characters needed.
          Or use the camera: every code in view is outlined and turns green once read.
        </p>
      </div>

      {latest && latest.ok === true && (
        <div className="panel">
          <div className="status ok">{latest.merged ? "Accepted · merged from two codes on the label" : `Accepted${latest.ms ? ` · ${latest.ms} ms` : ""}`}</div>
          <dl className="kv">
            <dt>Symbology</dt><dd>{latest.elements.symbologyId ?? "—"}</dd>
            <dt>GTIN</dt><dd>{latest.elements.gtin ?? "—"}</dd>
            <dt>SSCC</dt><dd>{latest.elements.sscc ?? "—"}</dd>
            <dt>Lot</dt><dd>{latest.elements.lot ?? "—"}</dd>
            <dt>Serial</dt><dd>{latest.elements.serial ?? "—"}</dd>
            <dt>Best before</dt><dd>{latest.elements.bestBefore ?? "—"}</dd>
            <dt>Expiry</dt><dd>{latest.elements.expiry ?? "—"}</dd>
            <dt>Production</dt><dd>{latest.elements.productionDate ?? "—"}</dd>
            <dt>Net weight</dt><dd>{latest.elements.netWeightKg !== null ? `${latest.elements.netWeightKg} kg` : "—"}</dd>
            <dt>Length</dt><dd>{latest.elements.lengthM !== null ? `${latest.elements.lengthM} m` : "—"}</dd>
            <dt>Count</dt><dd>{latest.elements.count ?? "—"}</dd>
            <dt>Identity ({scheme})</dt><dd>{identityKey(scheme, latest.elements) ?? "— (class level)"}</dd>
            <dt>Class</dt><dd>{classKey(latest.elements) ?? "—"}</dd>
          </dl>
          <ul className="hri">{latest.elements.hri.map((h, i) => <li key={i}>{h}</li>)}</ul>
        </div>
      )}
      {latest && latest.ok === "plain" && (
        <div className="panel">
          <div className="status attention">Plain code ({latest.symbology === "]A0" ? "Code 39" : latest.symbology === "]C0" ? "Code 128" : latest.symbology})</div>
          <p>
            <code>{latest.text}</code>
          </p>
          <p className="muted">Not GS1 data — a supplier's own identifier (e.g. carton number). The system will use these to look up containers.</p>
        </div>
      )}
      {latest && latest.ok === false && (
        <div className="panel">
          <div className="status bad">Rejected</div>
          <p>{latest.error}</p>
          {latest.markup && <p className="muted">{latest.markup}</p>}
          <p className="muted">Input: <code>{latest.input.replaceAll("\u001d", "⟨GS⟩")}</code></p>
        </div>
      )}

      {results.length > 1 && (
        <div className="panel">
          <p className="muted">Earlier scans</p>
          <ul className="hri">
            {results.slice(1).map((r, i) => (
              <li key={i}>{r.ok === true ? `✓ ${r.elements.gtin ?? r.elements.sscc ?? "?"} ${r.elements.lot ?? ""}` : r.ok === "plain" ? `· plain ${r.text}` : `✗ ${r.error}`}</li>
            ))}
          </ul>
        </div>
      )}
    </main>
  );
}
